/**
 * Engine HTTP client for the pipelines feature. Mirrors the routes mounted in
 * `services/engine/src/api/routes/pipelines.ts`:
 *
 *   GET  /api/pipelines                       → { items: Pipeline[] }
 *   POST /api/pipelines                       → { pipeline: Pipeline } (201)
 *   GET  /api/pipelines/:pipelineId           → { pipeline: Pipeline }
 *   GET  /api/pipelines/:pipelineId/adapter   → PipelineAdapter | 502
 *   GET  /api/pipelines/:pipelineId/statuses  → { items: InternalStatus[] }
 *   POST /api/pipelines/:pipelineId/statuses  → InternalStatus (201)
 *   PATCH /api/pipelines/:pipelineId/statuses/:statusId → InternalStatus
 *   DELETE /api/pipelines/:pipelineId/statuses/:statusId → InternalStatus | 409
 *   GET  /api/pipelines/:pipelineId/managers  → { items: Manager[] }
 *   GET  /api/pipelines/:pipelineId/leads     → { items: Lead[], page: { total, nextCursor } }
 *   GET  /api/pipelines/:pipelineId/leads/stats → LeadStats (filtered aggregates)
 *   GET  /api/pipelines/:pipelineId/leads/:leadId → Lead
 *   POST /api/pipelines/:pipelineId/leads     → Lead (201)
 *
 * Every fetcher runs the JSON body through a Zod schema before returning so
 * shape drift between engine and console fails loudly at the network boundary
 * (not deep inside a derived selector). Date columns arrive as ISO-8601
 * strings via Hono's default `JSON.stringify`; we coerce them back to `Date`
 * here so consumer code sees the same shape as `@ludus/core` types.
 */

import { z } from 'zod'
import { BOARD_WRITES } from '@ludus/core'
import type {
  AdapterCapabilities,
  BoardWrite,
  EngineRole,
  InternalStatus,
  Lead,
  LeadId,
  LudusLeadId,
  LeadStats,
  LeadStatsQuery,
  LifecyclePhase,
  ListLeadsPage,
  ListLeadsQuery,
  Manager,
  ManagerId,
  Pipeline,
  PipelineId,
  PipelineKind,
  PipelineOrigin,
  StatusId,
  ContactId,
} from '@ludus/core'
import { apiClient } from './client.ts'

// ──────────────────────────────────────────────────────────────────────────────
// Schemas
// ──────────────────────────────────────────────────────────────────────────────

const pipelineSchema = z.object({
  id: z.string(),
  name: z.string(),
  origin: z.string(),
  // `.catch` so an engine that predates this field degrades to the Home accent
  // instead of failing the whole catalogue parse.
  adapterKind: z.string().nullable().catch(null),
  kind: z.string().catch('deal'),
  isActive: z.boolean(),
})

// `lifecyclePhase`/`engineRole` (ADR-0003) are parsed leniently as strings and
// cast to the closed unions in `castStatus` — same boundary posture as branded
// ids. The engine is the source of truth for the enum membership and validates
// on write, so the console doesn't re-enumerate the literals here.
const internalStatusSchema = z.object({
  id: z.string(),
  pipelineId: z.string(),
  name: z.string(),
  position: z.number(),
  isActive: z.boolean(),
  lifecyclePhase: z.string(),
  engineRole: z.string(),
})

export const managerSchema = z.object({
  id: z.string(),
  pipelineIds: z.array(z.string()),
  firstName: z.string(),
  lastName: z.string(),
  middleName: z.string().nullable().default(null),
  phone: z.string().nullable().default(null),
  email: z.string().nullable().default(null),
  identities: z.array(
    z.object({
      adapterKind: z.string(),
      externalRef: z.string(),
      displayName: z.string().nullable(),
      email: z.string().nullable(),
    }),
  ),
  isActive: z.boolean(),
  isDeleted: z.boolean(),
  mergedInto: z.string().nullable().default(null),
})

// Engine emits Date columns as ISO-8601 strings (Hono default JSON.stringify).
// `z.coerce.date()` reverses that — `new Date(string)`. Nullable for `assignedAt`.
/** Exported beside {@link castLead}, so one card row has one reading. */
export const leadSchema = z.object({
  id: z.string(),
  pipelineId: z.string(),
  statusId: z.string(),
  assignedManagerId: z.string().nullable(),
  contactId: z.string().nullable(),
  name: z.string(),
  price: z.number().nullable(),
  funnel: z.string().nullable(),
  externalRef: z.string().nullable(),
  tags: z.array(z.string()),
  // Optional on the wire, not on the type: an engine older than the identity
  // layer omits it entirely, and a console bundle must not fail to parse a lead
  // because of a column it does not use yet. Same posture the event feed takes —
  // closed for writes, open for reads.
  ludusLeadId: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  assignedAt: z.coerce.date().nullable(),
})

const pipelinesEnvelopeSchema = z.object({ items: z.array(pipelineSchema) })
const pipelineAdapterSchema = z.object({
  adapterKind: z.string().nullable(),
  externalRef: z.string().nullable(),
  capabilities: z.object({
    writeStatus: z.boolean(),
    writeAssignment: z.boolean(),
    describeLead: z.boolean(),
  }),
})

const pipelineEnvelopeSchema = z.object({ pipeline: pipelineSchema })
const statusesEnvelopeSchema = z.object({ items: z.array(internalStatusSchema) })
const managersEnvelopeSchema = z.object({ items: z.array(managerSchema) })

// SBO-48/52: the leads list is paginated — `{ items, page: { total, nextCursor } }`.
const listLeadsPageSchema = z.object({
  items: z.array(leadSchema),
  page: z.object({ total: z.number(), nextCursor: z.string().nullable() }),
})

const leadStatsSchema = z.object({
  total: z.number(),
  totalValue: z.number(),
  byStage: z.array(
    z.object({
      statusId: z.string(),
      count: z.number(),
      totalValue: z.number(),
      medianAgeDays: z.number(),
      stuckCount: z.number(),
    }),
  ),
  byOwner: z.array(z.object({ managerId: z.string(), count: z.number(), totalValue: z.number() })),
  byFunnel: z.array(z.object({ funnel: z.string(), count: z.number() })),
  byAge: z.array(z.object({ bucket: z.string(), count: z.number() })),
})

// ──────────────────────────────────────────────────────────────────────────────
// Cast helpers
// ──────────────────────────────────────────────────────────────────────────────
//
// Zod returns `string`; the engine actually emits branded ids (`PipelineId`,
// `LeadId`, etc.). Branding is a compile-time-only discriminator, so the
// runtime value is identical and a single `as` cast at the boundary is the
// idiomatic, zero-cost way to recover the branded type. We do it here once,
// not at every call site.

function castPipeline(p: z.infer<typeof pipelineSchema>): Pipeline {
  return {
    ...p,
    id: p.id as PipelineId,
    origin: p.origin as PipelineOrigin,
    kind: p.kind as PipelineKind,
  }
}

function castStatus(s: z.infer<typeof internalStatusSchema>): InternalStatus {
  return {
    ...s,
    id: s.id as StatusId,
    pipelineId: s.pipelineId as PipelineId,
    lifecyclePhase: s.lifecyclePhase as LifecyclePhase,
    engineRole: s.engineRole as EngineRole,
  }
}

export function castManager(m: z.infer<typeof managerSchema>): Manager {
  return {
    ...m,
    id: m.id as ManagerId,
    pipelineIds: m.pipelineIds as PipelineId[],
    mergedInto: m.mergedInto as ManagerId | null,
  }
}

/**
 * Exported so the sale page (`ludus-leads.ts`) reads a card row through the
 * same cast the kanban and the lead detail use. A second reading of one row is
 * how two pages start disagreeing about one card.
 */
export function castLead(l: z.infer<typeof leadSchema>): Lead {
  return {
    ...l,
    id: l.id as LeadId,
    pipelineId: l.pipelineId as PipelineId,
    statusId: l.statusId as StatusId,
    assignedManagerId: l.assignedManagerId === null ? null : (l.assignedManagerId as ManagerId),
    contactId: l.contactId === null ? null : (l.contactId as ContactId),
    ludusLeadId: l.ludusLeadId == null ? null : (l.ludusLeadId as LudusLeadId),
  }
}

function castStats(s: z.infer<typeof leadStatsSchema>): LeadStats {
  return {
    total: s.total,
    totalValue: s.totalValue,
    byStage: s.byStage.map((r) => ({ ...r, statusId: r.statusId as StatusId })),
    byOwner: s.byOwner.map((r) => ({ ...r, managerId: r.managerId as ManagerId })),
    byFunnel: s.byFunnel,
    byAge: s.byAge,
  }
}

// Filter params shared by the list + stats reads. `managerId` is appended once
// per id (NOT bracketed) so the engine's `c.req.queries('managerId')` reads them.
function appendLeadFilters(params: URLSearchParams, query: ListLeadsQuery | LeadStatsQuery): void {
  if (query.statusId) params.set('statusId', query.statusId)
  for (const m of query.managerIds ?? []) params.append('managerId', m)
  if (query.period) params.set('period', query.period)
  if (query.q) params.set('q', query.q)
}

// ──────────────────────────────────────────────────────────────────────────────
// Errors
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Surface engine error envelopes (`{ error: '...' }` + non-2xx status) as a
 * typed error the SWR `error` channel can branch on without reaching back
 * into the axios response object. UI states (loading/empty/error/401) read
 * `kind` and `status`; `message` is for diagnostic logging only.
 *
 * The class name predates `fetchContact` (SBO-47) — `contacts.ts` reuses both
 * this class and `classifyApiError` via the `ApiError` alias-export below.
 * Full rename is deferred to a future cleanup.
 */
export class PipelineApiError extends Error {
  readonly kind:
    | 'unauthorized'
    | 'not_found'
    | 'auth_not_configured'
    | 'network'
    | 'write_not_carried'
    | 'source_unavailable'
    | 'unknown'
  readonly status: number | null
  readonly write: BoardWrite | null
  readonly source: string | null

  constructor(args: {
    kind: PipelineApiError['kind']
    status: number | null
    message: string
    write?: BoardWrite
    source?: string
  }) {
    super(args.message)
    this.name = 'PipelineApiError'
    this.kind = args.kind
    this.status = args.status
    this.write = args.write ?? null
    this.source = args.source ?? null
  }
}

const writeNotCarriedSchema = z.object({
  error: z.literal('write_not_carried'),
  write: z.enum(BOARD_WRITES as [BoardWrite, ...BoardWrite[]]),
  source: z.string(),
})

export function classifyApiError(err: unknown): PipelineApiError {
  // Tested before the transport branch: a `ZodError` has no `.response`, so
  // reading `status` off it would call a contract mismatch a network fault.
  if (err instanceof z.ZodError) {
    return new PipelineApiError({ kind: 'unknown', status: null, message: 'malformed response' })
  }
  // Axios attaches `response` on HTTP errors; without it, treat as transport-level.
  const e = err as { response?: { status?: number; data?: { error?: string } }; message?: string }
  const status = e.response?.status ?? null
  const code = e.response?.data?.error
  if (status === 401) {
    return new PipelineApiError({ kind: 'unauthorized', status, message: 'unauthorized' })
  }
  if (status === 404) {
    return new PipelineApiError({ kind: 'not_found', status, message: 'not_found' })
  }
  if (status === 409) {
    const refused = writeNotCarriedSchema.safeParse(e.response?.data)
    if (refused.success) {
      return new PipelineApiError({
        kind: 'write_not_carried',
        status,
        message: 'write_not_carried',
        write: refused.data.write,
        source: refused.data.source,
      })
    }
  }
  if (status === 502 && code === 'source_unavailable') {
    return new PipelineApiError({ kind: 'source_unavailable', status, message: code })
  }
  if (status === 500 && code === 'auth_not_configured') {
    return new PipelineApiError({
      kind: 'auth_not_configured',
      status,
      message: 'auth_not_configured',
    })
  }
  if (status === null) {
    return new PipelineApiError({
      kind: 'network',
      status: null,
      message: e.message ?? 'network error',
    })
  }
  return new PipelineApiError({
    kind: 'unknown',
    status,
    message: e.message ?? `http ${status}`,
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// Fetchers — SWR-friendly (key-based)
// ──────────────────────────────────────────────────────────────────────────────
//
// SWR calls a fetcher with the cache key. We use the URL as the key, so the
// fetcher signature is `(url: string) => Promise<T>`. Branded-id `PipelineId`
// values stringify to their underlying string at the route boundary.

export async function listPipelines(opts: { includeArchived?: boolean } = {}): Promise<Pipeline[]> {
  try {
    const { data } = await apiClient.get('/pipelines', {
      params: opts.includeArchived ? { includeArchived: 'true' } : undefined,
    })
    return pipelinesEnvelopeSchema.parse(data).items.map(castPipeline)
  } catch (err) {
    throw classifyApiError(err)
  }
}

export async function fetchPipeline(pipelineId: PipelineId): Promise<Pipeline> {
  try {
    const { data } = await apiClient.get(`/pipelines/${pipelineId}`)
    return castPipeline(pipelineEnvelopeSchema.parse(data).pipeline)
  } catch (err) {
    throw classifyApiError(err)
  }
}

export interface PipelineAdapter {
  adapterKind: string | null
  /** `null` for a board that mirrors nothing — native, or disconnected. */
  externalRef: string | null
  capabilities: Pick<AdapterCapabilities, 'writeStatus' | 'writeAssignment' | 'describeLead'>
}

export async function fetchPipelineAdapter(pipelineId: PipelineId): Promise<PipelineAdapter> {
  try {
    const { data } = await apiClient.get(`/pipelines/${pipelineId}/adapter`)
    return pipelineAdapterSchema.parse(data)
  } catch (err) {
    throw classifyApiError(err)
  }
}

export async function fetchPipelineStatuses(pipelineId: PipelineId): Promise<InternalStatus[]> {
  try {
    const { data } = await apiClient.get(`/pipelines/${pipelineId}/statuses`)
    return statusesEnvelopeSchema.parse(data).items.map(castStatus)
  } catch (err) {
    throw classifyApiError(err)
  }
}

export async function fetchPipelineManagers(pipelineId: PipelineId): Promise<Manager[]> {
  try {
    const { data } = await apiClient.get(`/pipelines/${pipelineId}/managers`)
    return managersEnvelopeSchema.parse(data).items.map(castManager)
  } catch (err) {
    throw classifyApiError(err)
  }
}

export async function fetchPipelineLeads(
  pipelineId: PipelineId,
  query: ListLeadsQuery = {},
): Promise<ListLeadsPage<Lead>> {
  const params = new URLSearchParams()
  appendLeadFilters(params, query)
  if (query.sort) params.set('sort', query.sort)
  if (query.dir) params.set('dir', query.dir)
  if (query.limit !== undefined) params.set('limit', String(query.limit))
  if (query.offset !== undefined) params.set('offset', String(query.offset))
  if (query.cursor) params.set('cursor', query.cursor)
  const qs = params.toString()
  try {
    const { data } = await apiClient.get(`/pipelines/${pipelineId}/leads${qs ? `?${qs}` : ''}`)
    const parsed = listLeadsPageSchema.parse(data)
    return {
      items: parsed.items.map(castLead),
      total: parsed.page.total,
      nextCursor: parsed.page.nextCursor,
    }
  } catch (err) {
    throw classifyApiError(err)
  }
}

export async function fetchLeadStats(
  pipelineId: PipelineId,
  query: LeadStatsQuery = {},
): Promise<LeadStats> {
  const params = new URLSearchParams()
  appendLeadFilters(params, query)
  const qs = params.toString()
  try {
    const { data } = await apiClient.get(
      `/pipelines/${pipelineId}/leads/stats${qs ? `?${qs}` : ''}`,
    )
    return castStats(leadStatsSchema.parse(data))
  } catch (err) {
    throw classifyApiError(err)
  }
}

export async function fetchLead(pipelineId: PipelineId, leadId: LeadId): Promise<Lead> {
  try {
    const { data } = await apiClient.get(`/pipelines/${pipelineId}/leads/${leadId}`)
    return castLead(leadSchema.parse(data))
  } catch (err) {
    throw classifyApiError(err)
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Mutations
// ──────────────────────────────────────────────────────────────────────────────

export interface CreatePipelineInput {
  name: string
  origin: PipelineOrigin
}

export async function createPipeline(input: CreatePipelineInput): Promise<Pipeline> {
  try {
    const { data } = await apiClient.post('/pipelines', input)
    return castPipeline(pipelineEnvelopeSchema.parse(data).pipeline)
  } catch (err) {
    throw classifyApiError(err)
  }
}

/**
 * Wire payload for `POST /api/pipelines/:pipelineId/leads`. Mirrors the
 * engine-side `CreateLeadInput` minus `contactId` — the console has no
 * contacts UI yet, so we don't surface that field. Branding survives at the
 * type level; the engine accepts raw strings and re-brands.
 */
export interface CreateLeadInput {
  statusId: StatusId
  assignedManagerId: ManagerId
  name: string
  price?: number | null
  funnel?: string | null
  externalRef?: string | null
  tags?: string[]
}

export async function createLead(pipelineId: PipelineId, input: CreateLeadInput): Promise<Lead> {
  try {
    const { data } = await apiClient.post(`/pipelines/${pipelineId}/leads`, input)
    return castLead(leadSchema.parse(data))
  } catch (err) {
    throw classifyApiError(err)
  }
}

/**
 * Wire payload for `PATCH /api/pipelines/:pipelineId/leads/:leadId` (deal-detail
 * inline edits + stage move). Every field optional; the engine requires at
 * least one and validates a supplied status/owner. `externalRef` is omitted —
 * a CRM ref, not user-editable from the console.
 */
export interface UpdateLeadInput {
  name?: string
  price?: number | null
  funnel?: string | null
  tags?: string[]
  statusId?: StatusId
  assignedManagerId?: ManagerId
}

export async function updateLead(
  pipelineId: PipelineId,
  leadId: LeadId,
  input: UpdateLeadInput,
): Promise<Lead> {
  try {
    const { data } = await apiClient.patch(`/pipelines/${pipelineId}/leads/${leadId}`, input)
    return castLead(leadSchema.parse(data))
  } catch (err) {
    throw classifyApiError(err)
  }
}

export interface CreateStatusInput {
  name: string
  lifecyclePhase?: LifecyclePhase
  engineRole?: EngineRole
}

export async function createStatus(
  pipelineId: PipelineId,
  input: CreateStatusInput,
): Promise<InternalStatus> {
  try {
    const { data } = await apiClient.post(`/pipelines/${pipelineId}/statuses`, input)
    return castStatus(internalStatusSchema.parse(data))
  } catch (err) {
    throw classifyApiError(err)
  }
}

/**
 * Wire payload for `PATCH /api/pipelines/:pipelineId/statuses/:statusId` — the
 * stage's name and its ADR-0003 classification. Every field optional; the
 * engine requires at least one, and rejects a blank name or an unknown enum
 * value with 400.
 */
export interface UpdateStatusInput {
  name?: string
  lifecyclePhase?: LifecyclePhase
  engineRole?: EngineRole
}

export async function updateStatus(
  pipelineId: PipelineId,
  statusId: StatusId,
  input: UpdateStatusInput,
): Promise<InternalStatus> {
  try {
    const { data } = await apiClient.patch(`/pipelines/${pipelineId}/statuses/${statusId}`, input)
    return castStatus(internalStatusSchema.parse(data))
  } catch (err) {
    throw classifyApiError(err)
  }
}

/**
 * A stage that still holds leads is not a failed delete: the engine answers 409
 * with the live count so the caller can ask where those leads should go and
 * retry with `migrateTo`. Every other failure still throws `PipelineApiError`.
 */
export type DeactivateStatusResult =
  | { deactivated: true; status: InternalStatus }
  | { deactivated: false; leadCount: number }

const statusInUseSchema = z.object({
  error: z.literal('status_in_use'),
  leadCount: z.number().int().nonnegative(),
})

export async function deactivateStatus(
  pipelineId: PipelineId,
  statusId: StatusId,
  opts: { migrateTo?: StatusId } = {},
): Promise<DeactivateStatusResult> {
  try {
    const { data } = await apiClient.delete(`/pipelines/${pipelineId}/statuses/${statusId}`, {
      params: opts.migrateTo ? { migrateTo: opts.migrateTo } : undefined,
    })
    return { deactivated: true, status: castStatus(internalStatusSchema.parse(data)) }
  } catch (err) {
    const body = (err as { response?: { status?: number; data?: unknown } }).response
    if (body?.status === 409) {
      const inUse = statusInUseSchema.safeParse(body.data)
      if (inUse.success) return { deactivated: false, leadCount: inUse.data.leadCount }
    }
    throw classifyApiError(err)
  }
}

/**
 * Archiving and removal (#133). Two calls rather than one flag, because they
 * are two acts: archiving hides a board and keeps everything, removal erases a
 * board that never held a card.
 */
export async function setPipelineActive(
  pipelineId: PipelineId,
  isActive: boolean,
): Promise<Pipeline> {
  try {
    const { data } = await apiClient.patch(`/pipelines/${pipelineId}`, { isActive })
    return castPipeline(pipelineEnvelopeSchema.parse(data).pipeline)
  } catch (err) {
    throw classifyApiError(err)
  }
}

/** Read as a plain string: a blocker kind the engine adds must show as one row, never blank the dialog. */
const removalBlockerSchema = z.object({
  kind: z.string(),
  count: z.number().int().nonnegative(),
})

const removalReportSchema = z.object({
  canDelete: z.boolean(),
  blockers: z.array(removalBlockerSchema),
  holdings: z.object({
    cards: z.number().int().nonnegative(),
    stages: z.number().int().nonnegative(),
    hasConnection: z.boolean(),
    runs: z.number().int().nonnegative(),
    rawObjects: z.number().int().nonnegative(),
  }),
})

export type RemovalBlocker = z.infer<typeof removalBlockerSchema>
export type RemovalReport = z.infer<typeof removalReportSchema>

export async function fetchRemovalReport(pipelineId: PipelineId): Promise<RemovalReport> {
  try {
    const { data } = await apiClient.get(`/pipelines/${pipelineId}/removal`)
    return removalReportSchema.parse(data)
  } catch (err) {
    throw classifyApiError(err)
  }
}

export type DeletePipelineResult =
  | { deleted: true }
  | { deleted: false; blockers: RemovalBlocker[] }

const notEmptySchema = z.object({
  error: z.literal('pipeline_not_empty'),
  blockers: z.array(removalBlockerSchema),
})

/**
 * The refusal comes back as a value, not an exception, the way `deactivateStatus`
 * hands back its lead count: the dialog has something to say either way, and a
 * 409 here is an answer rather than a failure.
 */
export async function deletePipeline(pipelineId: PipelineId): Promise<DeletePipelineResult> {
  try {
    await apiClient.delete(`/pipelines/${pipelineId}`)
    return { deleted: true }
  } catch (err) {
    const res = (err as { response?: { status?: number; data?: unknown } }).response
    if (res?.status === 409) {
      const refused = notEmptySchema.safeParse(res.data)
      if (refused.success) return { deleted: false, blockers: refused.data.blockers }
    }
    throw classifyApiError(err)
  }
}

// Less-misleading alias used by cross-resource consumers (e.g. `contacts.ts`).
export { PipelineApiError as ApiError }
